import "./highlightProducts.scss";

import { View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { TrafficFrom } from "common/helpers/traffic-from";
import { CatalogicalItem, convertToSimplified } from "common/types/catalogical-type";
import { getString } from "common/uistringlabels/uiStringUtils";
import { config } from "config";
import NoLogoImage from "img/no_logo_image.png";
import { SearchExperienceService } from "lib/services/shop-search-service";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import React, { FC, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

const HIGHLIGHT = {
  ITEM_TYPE: "software" as const,
  MODE: "highlight" as const,
  LIMIT: 5,
  EMPTY_STRING: "" as const,
};

const ANALYTICS = {
  SECTION: "highlight-products",
  EVENT_NAME: "highlight-product-click",
};

const TEST_IDS = {
  ROOT: "highlight-products",
  PRODUCT_PREFIX: "highlight-product-",
};

const STRING_LABEL_PATH = "searchResults.searchResult";
const HIGHLIGHT_STRINGS = {
  title: `${STRING_LABEL_PATH}.highlightProductsTitle`,
  subtitle: `${STRING_LABEL_PATH}.highlightProductsSubtitle`,
  categoryTitle: `${STRING_LABEL_PATH}.highlightProductsCategoryTitle`,
};

const ROUTES = {
  detailsBase: "items/",
};

interface HighlightProduct {
  itemId: string;
  name: string;
  logoImageUrl: string;
  price?: string;
  categories?: string[];
}

interface HighlightProductsProps {
  isLoading?: boolean;
  categories?: string[];
  searchQuery?: string;
}

// Helper for the compact category label in the title
const buildCategoryLabel = (rawCategories: string[] = []) => {
  const uniq = Array.from(new Set(rawCategories.filter(Boolean)));
  if (uniq.length === 0) return "";
  if (uniq.length === 1) return uniq[0];
  return `${uniq[0]} and ${uniq.length - 1} more`;
};

export const HighlightProducts: FC<HighlightProductsProps> = ({
  isLoading = false,
  categories = [],
  searchQuery = "",
}) => {
  const [products, setProducts] = useState<HighlightProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const history = useHistory();
  const gridRef = useRef<HTMLDivElement>(null);

  // Only render when browse-all OR has category filters
  const shouldShow = searchQuery.trim() === "" || (categories && categories.length > 0);

  const showCategoryTitle = categories.length > 0;
  const categoryLabel = buildCategoryLabel(categories);
  const headerTitleText = showCategoryTitle
    ? getString(HIGHLIGHT_STRINGS.categoryTitle, { category: categoryLabel })
    : getString(HIGHLIGHT_STRINGS.title);

  useEffect(() => {
    const fetchHighlightProducts = async () => {
      if (!shouldShow) {
        setProducts([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const searchService = new SearchExperienceService("software");

        const request: any = {
          itemType: HIGHLIGHT.ITEM_TYPE,
          searchQuery: HIGHLIGHT.EMPTY_STRING,
          useSemanticSearch: false,
          simplifiedResults: true,
          caseInsensitive: true,
          limit: HIGHLIGHT.LIMIT,
          mode: HIGHLIGHT.MODE,
          ...(categories.length > 0 ? { filter: JSON.stringify({ categories }) } : {}),
        };

        const result = await searchService.getItems<CatalogicalItem>(request);

        if (result && result.data) {
          const simplifiedItems = convertToSimplified(result.data);
          const highlightProducts = simplifiedItems.slice(0, HIGHLIGHT.LIMIT).map((item) => ({
            itemId: item.itemId || "",
            name: item.name || "",
            logoImageUrl: item.logoImageUrl || "",
            price: item.price,
            categories: item.categories,
          }));
          setProducts(highlightProducts);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching highlight products:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHighlightProducts();
  }, [shouldShow, categories, searchQuery]);

  const trackProductClick = (product: HighlightProduct, index: number) => {
    const trackerId = config.analytics.uxmAppId;
    const payload = {
      productName: product.name,
      productId: product.itemId,
      section: ANALYTICS.SECTION,
      position: index + 1,
    };
    trackCustomEvent(payload, ANALYTICS.EVENT_NAME, trackerId);
  };

  const handleProductClick = (product: HighlightProduct, index: number, event?: React.MouseEvent) => {
    if (event && (event.ctrlKey || event.metaKey || event.button === 1)) {
      return;
    }

    if (event) {
      event.preventDefault();
    }

    trackProductClick(product, index);
    try {
      const detailsUrl = `${ROUTES.detailsBase}${ItemTaxonomyId.Parse(product.itemId).getUrlSuffix()}?from=${
        TrafficFrom.Search
      }`;
      history.push(detailsUrl);
    } catch (error) {
      console.error("Error navigating to product:", error);
    }
  };

  const getProductUrl = (product: HighlightProduct) => {
    try {
      return `${ROUTES.detailsBase}${ItemTaxonomyId.Parse(product.itemId).getUrlSuffix()}?from=${TrafficFrom.Search}`;
    } catch (error) {
      console.error("Error generating product URL:", error);
      return "#";
    }
  };

  if (!shouldShow) return null;

  if (loading || isLoading) {
    return (
      <View
        className="highlight-loading"
        display="flex"
        justifyContent="center"
        alignItems="center"
        padding="S600"
        minHeight="160px"
      >
        <Spinner size={SpinnerSize.Small} />
      </View>
    );
  }

  if (products.length === 0) return null;

  return (
    <View className="highlight-products-wrapper" dataTestId={TEST_IDS.ROOT}>
      <View className="highlight-header">
        <View>
          <Text fontSize="T300" fontWeight="bold" color="neutral90">
            {headerTitleText}
          </Text>
        </View>
        <View>
          <Text fontSize="T100" color="neutral60">
            {getString(HIGHLIGHT_STRINGS.subtitle)}
          </Text>
        </View>
      </View>

      <View className="products-grid" ref={gridRef}>
        {products.map((product, index) => (
          <a
            key={product.itemId}
            href={getProductUrl(product)}
            className={`product-card ${hoveredIndex === index ? "hovered" : ""}`}
            onClick={(event) => handleProductClick(product, index, event)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            data-testid={`${TEST_IDS.PRODUCT_PREFIX}${product.itemId}`}
          >
            <View className="product-content">
              <View className="image-wrapper">
                <View className="img-square-wrapper">
                  <img
                    alt={product.name}
                    src={product.logoImageUrl || NoLogoImage}
                    className="product-image"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = NoLogoImage;
                    }}
                  />
                </View>
              </View>

              <View className="product-info">
                <Text className="product-name" color="neutral80" title={product.name}>
                  {product.name}
                </Text>
              </View>
            </View>
          </a>
        ))}
      </View>
    </View>
  );
};
